import { Grid, Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { isValid } from "date-fns";
import { useIntl } from "react-intl";
import { standardDateFormat } from "utils/dateUtils";

type DateRangeFilterProps = {
  label?: string;
  fromValue: Date | undefined;
  toValue: Date | undefined;
  onFromChange: (date: Date | undefined) => void;
  onToChange: (date: Date | undefined) => void;
  disabled?: boolean;
  fromLabel?: string;
  toLabel?: string;
};

const DateRangeFilter = ({
  label,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  disabled = false,
  fromLabel,
  toLabel,
}: DateRangeFilterProps) => {
  const { formatMessage: t } = useIntl();

  const handleFromChange = (date: any) => {
    if (isValid(date)) {
      onFromChange(date);
    } else if (date == null) {
      onFromChange(undefined);
    }
  };

  const handleToChange = (date: any) => {
    if (isValid(date)) {
      onToChange(date);
    } else if (date == null) {
      onToChange(undefined);
    }
  };

  const styles = {
    container: {
      border: 1,
      borderColor: "#c4c4c4",
      borderRadius: 1,
      paddingX: "14px",
      pt: "7px",
      pb: "8px",
      marginBottom: "-16px",
      backgroundColor: "background.paper",
      transition: "border-color 0.2s ease-in-out",
      "&:hover": {
        borderColor: "text.primary",
      },
    },
    labelGrid: {
      paddingBottom: "0px !important",
    },
    gridItem: label ? { paddingTop: "0 !important" } : {},
    inputBase: {
      "& .MuiInputBase-input": {
        padding: 0,
      },
    },
  };

  return (
    <Box sx={styles.container}>
      <Grid container spacing={2}>
        {label && (
          <Grid item xs={12} sx={styles.labelGrid}>
            <label>{label}</label>
          </Grid>
        )}
        <Grid item xs={12} sm={6} sx={styles.gridItem}>
          <DatePicker
            label={fromLabel || t({ id: "fromLabel" })}
            disabled={disabled}
            value={fromValue || null}
            onChange={handleFromChange}
            format={standardDateFormat}
            maxDate={toValue || undefined}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "standard",
                size: "small",
                InputProps: {
                  sx: styles.inputBase,
                },
              },
              actionBar: {
                actions: ["clear", "cancel"],
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} sx={styles.gridItem}>
          <DatePicker
            label={toLabel || t({ id: "toLabel" })}
            disabled={disabled}
            value={toValue || null}
            onChange={handleToChange}
            format={standardDateFormat}
            minDate={fromValue || undefined}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "standard",
                size: "small",
                InputProps: {
                  sx: styles.inputBase,
                },
              },
              actionBar: {
                actions: ["clear", "cancel"],
              },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DateRangeFilter;
